import { useEffect, useRef, useState } from 'react';
import { QuestionMarkCircleIcon, MapIcon } from '@heroicons/react/24/outline';
import { useTour } from './TourProvider';

/**
 * The header's "?" button: replay the tour of this page. The Getting started checklist opens
 * from the sidebar's Guided tour button instead. Styled like the shared UserMenu dropdown it sits beside.
 */
export default function HelpMenu() {
  const { startPageTour, pageTourTitle } = useTour();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const item = 'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] transition-colors';

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-tour="tour-help"
        className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        title="Help & tours"
        aria-label="Help & tours"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <QuestionMarkCircleIcon className="h-5 w-5" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-64 overflow-hidden rounded-xl border border-border bg-card shadow-lg z-50"
        >
          <div className="space-y-0.5 p-1.5">
            <button
              role="menuitem"
              disabled={!pageTourTitle}
              onClick={() => { setOpen(false); startPageTour(); }}
              className={`${item} ${pageTourTitle ? 'text-foreground hover:bg-muted' : 'cursor-not-allowed text-muted-foreground opacity-60'}`}
              title={pageTourTitle ? undefined : 'This page has no tour yet'}
            >
              <MapIcon className="h-[18px] w-[18px] flex-shrink-0" />
              <span className="truncate">
                {pageTourTitle ? `Tour this page (${pageTourTitle})` : 'Tour this page'}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
